import React, { useMemo } from "react";
import { Textarea, Typography } from "@sumprio/ui";
import { SideModal, LimitedCharInput } from "components/common";
import {
  MAX_CATEGORY_ENTITY_NAME_CHARS,
  MAX_CATEGORY_ENTITY_SHORTCODE_CHARS,
} from "constants/formTextLimits";
import { exceedsMaxLength } from "utils/limitedString";
import SideModalFormFooter, { type SideModalFormFooterProps } from "./SideModalFormFooter";

export interface CategoryFormErrorsShape {
  nameErr: string;
  shortcodeErr: string;
  descriptionErr: string;
}

export type CategoryModalVariant = "category" | "subcategory" | "service";
export type CategoryModalMode = "add" | "edit";

const COPY: Record<
  CategoryModalVariant,
  Record<
    CategoryModalMode,
    {
      title: string;
      primaryLabel: string;
      intro: string;
      nameLabel: string;
      namePlaceholder: string;
    }
  >
> = {
  category: {
    add: {
      title: "Add New Category",
      primaryLabel: "Add Category",
      intro:
        "Create a category to organize cases and streamline how they are managed across the platform.",
      nameLabel: "Category Name",
      namePlaceholder: "e.g. Billing & Invoices",
    },
    edit: {
      title: "Edit Category",
      primaryLabel: "Update Category",
      intro: "Update the category details to keep your structure organized and up to date.",
      nameLabel: "Category Name",
      namePlaceholder: "e.g. Billing & Invoices",
    },
  },
  subcategory: {
    add: {
      title: "Add New Sub Category",
      primaryLabel: "Add Sub Category",
      intro:
        "Create a sub category to further organize services under this category and improve structure.",
      nameLabel: "Sub Category Name",
      namePlaceholder: "e.g. Domestic Shipments",
    },
    edit: {
      title: "Edit Sub Category",
      primaryLabel: "Update Sub Category",
      intro: "Update the sub category details to keep your structure organized and accurate.",
      nameLabel: "Sub Category Name",
      namePlaceholder: "e.g. Domestic Shipments",
    },
  },
  service: {
    add: {
      title: "Add New Service",
      primaryLabel: "Add Service",
      intro: "Create a service to define specific offerings and streamline operations.",
      nameLabel: "Service Name",
      namePlaceholder: "e.g. Same-Day Delivery",
    },
    edit: {
      title: "Edit Service",
      primaryLabel: "Update Service",
      intro: "Update the service details to keep your offerings accurate and up to date.",
      nameLabel: "Service Name",
      namePlaceholder: "e.g. Same-Day Delivery",
    },
  },
};

const SHORTCODE_PLACEHOLDER = "e.g. DOMES_101";
const CATEGORY_SHORTCODE_PLACEHOLDER = "e.g. BILLING_101";
const SERVICE_SHORTCODE_PLACEHOLDER = "e.g. SAMEDAY_101";

export interface AddEditCategoryEntityModalProps {
  open: boolean;
  variant: CategoryModalVariant;
  mode: CategoryModalMode;
  name: string;
  shortcode: string;
  description: string;
  errors: CategoryFormErrorsShape;
  introOverride?: string;
  onClose: () => void;
  onClear: () => void;
  onNameChange: (_value: string) => void;
  onShortcodeChange: (_value: string) => void;
  onDescriptionChange: (_value: string) => void;
  onSubmit: () => void;
  primaryDisabled?: boolean;
  submitting?: boolean;
  primaryButtonVariant?: SideModalFormFooterProps["primaryVariant"];
}

export default function AddEditCategoryEntityModal({
  open,
  variant,
  mode,
  name,
  shortcode,
  description,
  errors,
  onClose,
  onClear,
  onNameChange,
  onShortcodeChange,
  onDescriptionChange,
  onSubmit,
  introOverride,
  primaryDisabled = false,
  submitting = false,
  primaryButtonVariant = "brand-default",
}: AddEditCategoryEntityModalProps) {
  const copy = useMemo(() => COPY[variant][mode], [variant, mode]);
  const intro = introOverride ?? copy.intro;
  const nameTrimmed = name?.trim() ?? "";
  const nameOk = !!nameTrimmed && !exceedsMaxLength(nameTrimmed, MAX_CATEGORY_ENTITY_NAME_CHARS);
  const shortcodeTrimmed = shortcode?.trim() ?? "";
  const shortcodeOk =
    !!shortcodeTrimmed && !exceedsMaxLength(shortcodeTrimmed, MAX_CATEGORY_ENTITY_SHORTCODE_CHARS);
  const isFormComplete = !!(nameOk && shortcodeOk && description?.trim());
  const isPrimaryDisabled = !isFormComplete || primaryDisabled || submitting;
  const shortcodePlaceholder =
    variant === "category"
      ? CATEGORY_SHORTCODE_PLACEHOLDER
      : variant === "service"
        ? SERVICE_SHORTCODE_PLACEHOLDER
        : SHORTCODE_PLACEHOLDER;

  const footer = (
    <SideModalFormFooter
      onCancel={onClose}
      cancelDisabled={submitting}
      onMiddle={onClear}
      middleDisabled={submitting}
      onPrimary={onSubmit}
      primaryDisabled={isPrimaryDisabled}
      primaryLabel={submitting ? "Saving..." : copy.primaryLabel}
      primaryVariant={primaryButtonVariant}
    />
  );

  return (
    <SideModal
      open={open}
      onClose={onClose}
      title={copy.title}
      footer={footer}
      sideSize="md"
      showFooterDivider
    >
      <Typography variant="body" bodySize="sm" bodyWeight="regular">
        {intro}
      </Typography>
      <LimitedCharInput
        size="sm"
        label={copy.nameLabel}
        required
        placeholder={copy.namePlaceholder}
        value={name}
        onValueChange={onNameChange}
        maxLength={MAX_CATEGORY_ENTITY_NAME_CHARS}
        showCharacterCount
        error={!!errors.nameErr}
        errorMessage={errors.nameErr}
        marginY="md"
        width="full"
      />
      <LimitedCharInput
        size="sm"
        label="Shortcode"
        required
        placeholder={shortcodePlaceholder}
        value={shortcode}
        onValueChange={onShortcodeChange}
        maxLength={MAX_CATEGORY_ENTITY_SHORTCODE_CHARS}
        showCharacterCount
        error={!!errors.shortcodeErr}
        errorMessage={errors.shortcodeErr}
        marginY="md"
        width="full"
      />
      <Textarea
        size="sm"
        label="Description"
        required
        placeholder="Enter a description..."
        value={description}
        onChange={(e) => onDescriptionChange((e.target as HTMLTextAreaElement).value)}
        error={!!errors.descriptionErr}
        errorMessage={errors.descriptionErr}
      />
    </SideModal>
  );
}
